import type { ColDef } from "ag-grid-community";
import { Typography } from "antd";
import React, { ReactNode } from "react";
import Grid from "../../atoms/Grid";
import { SubText } from "../../atoms/SubText";
import { ButtonGroup, ButtonGroupProps } from "../Button/ButtonGroup";
import { CONFIRM_GRID_FIXED_HEIGHT_PX, CONFIRM_GRID_HEADER_PX, CONFIRM_GRID_ROW_PX } from "./constants";

type SlotName = "subText" | "grid" | "actions";

type SlotComponent<P = object> = React.FC<P> & { __confirmGridModalSlot?: SlotName };

type RootProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

export type ConfirmGridModalGridProps = {
  columns: ColDef[];
  data: unknown[];
  isEmpty?: boolean;
  emptyMessage?: ReactNode;
  className?: string;
  height?: number | string;
  headerHeight?: number;
  rowHeight?: number;
  gridProps?: Record<string, unknown>;
};

const emptyStateContainerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 12px",
  textAlign: "center",
};

const SubTextSlot: SlotComponent<{ children: ReactNode }> = ({ children }) => <SubText>{children}</SubText>;
SubTextSlot.__confirmGridModalSlot = "subText";

const GridSlot: SlotComponent<ConfirmGridModalGridProps> = (props) => {
  const {
    columns,
    data,
    isEmpty = false,
    emptyMessage,
    className,
    height = CONFIRM_GRID_FIXED_HEIGHT_PX,
    headerHeight = CONFIRM_GRID_HEADER_PX,
    rowHeight = CONFIRM_GRID_ROW_PX,
    gridProps,
  } = props;

  const { className: gridInnerClassName, ...restGridProps } = gridProps ?? {};
  const gridClassNames = ["pl-0 pr-0", gridInnerClassName].filter(Boolean).join(" ");

  return (
    <div className={className} style={{ height }}>
      {isEmpty ? (
        <div style={{ height, ...emptyStateContainerStyle }}>
          <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
            {emptyMessage}
          </Typography.Paragraph>
        </div>
      ) : (
        <Grid
          className={gridClassNames}
          columns={columns}
          data={data}
          height={height}
          headerHeight={headerHeight}
          rowHeight={rowHeight}
          {...restGridProps}
        />
      )}
    </div>
  );
};
GridSlot.__confirmGridModalSlot = "grid";

const ActionsSlot: SlotComponent<ButtonGroupProps> = (props) => <ButtonGroup {...props} />;
ActionsSlot.__confirmGridModalSlot = "actions";

function pickSlots(children: React.ReactNode) {
  const slots: Partial<Record<SlotName, React.ReactElement>> = {};

  type WithSlot = { __confirmGridModalSlot?: SlotName };

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const slot = (child.type as WithSlot).__confirmGridModalSlot as SlotName | undefined;
    if (!slot) return;
    if (!slots[slot]) slots[slot] = child;
  });

  return slots;
}

const Root: React.FC<RootProps> = ({ children, className, style }) => {
  const { subText, grid, actions } = pickSlots(children);

  return (
    <div className={className} style={style}>
      {subText ?? null}
      {grid ?? null}
      {actions ?? null}
    </div>
  );
};

type ConfirmGridModalComponent = React.FC<RootProps> & {
  SubText: typeof SubTextSlot;
  Grid: typeof GridSlot;
  Actions: typeof ActionsSlot;
};

export const ConfirmGridModal = Root as ConfirmGridModalComponent;
ConfirmGridModal.SubText = SubTextSlot;
ConfirmGridModal.Grid = GridSlot;
ConfirmGridModal.Actions = ActionsSlot;
